import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { PARK } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_ICON = {
  google: { label: "Google", bg: "#0F0E0E", fg: "#F5EFE6", char: "G" },
  pitchup: { label: "Pitchup", bg: "#B76E79", fg: "#F5EFE6", char: "P" },
  manual: { label: "Verified guest", bg: "#3B8C4E", fg: "#F5EFE6", char: "✓" },
  other: { label: "Guest", bg: "#6A5F58", fg: "#F5EFE6", char: "•" },
};

const Reviews = () => {
  const socials = PARK.socials || {};
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data || [])).catch(() => setReviews([]));
  }, []);

  // Auto-advance every 7s
  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 7000);
    return () => clearInterval(t);
  }, [reviews.length]);

  const hasReviews = reviews.length > 0;
  if (!socials.googleReviews && !socials.pitchup && !hasReviews) return null;

  const current = reviews[idx];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative bg-[#F5EFE6] py-16 md:py-24"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <p className="overline text-[#B76E79] justify-center inline-flex items-center gap-2">
          <span className="marker-line" /> Loved by guests
        </p>
        <h2 className="mt-4 font-serif text-3xl md:text-5xl text-[#0F0E0E] leading-[1.05]">
          Read what our visitors are saying
        </h2>
        <div className="mt-4 flex items-center justify-center gap-1 text-[#B76E79]">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} size={18} fill="currentColor" strokeWidth={0} />
          ))}
        </div>

        {/* Carousel */}
        {hasReviews && current && (
          <div
            data-testid="review-carousel"
            className="mt-10 bg-white border border-[#DDD1BE] rounded-2xl p-8 md:p-12 relative overflow-hidden"
          >
            <Quote size={32} className="text-[#B76E79]/40 absolute top-6 left-8" strokeWidth={1.5} />
            <div key={idx} className="review-slide">
              <p className="mt-4 text-[#3A322E] text-lg md:text-xl leading-relaxed italic max-w-3xl mx-auto min-h-[100px]">
                &ldquo;{current.quote}&rdquo;
              </p>
              <div className="mt-6 flex items-center justify-center gap-1 text-[#B76E79]">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    size={14}
                    fill={i < (current.rating || 5) ? "currentColor" : "none"}
                    strokeWidth={1.5}
                  />
                ))}
              </div>
            <div className="mt-3 flex items-center justify-center gap-2 text-sm">
              <span
                className="w-5 h-5 rounded-full inline-flex items-center justify-center text-xs font-bold"
                style={{
                  background: (SOURCE_ICON[current.source] || SOURCE_ICON.other).bg,
                  color: (SOURCE_ICON[current.source] || SOURCE_ICON.other).fg,
                }}
              >
                {(SOURCE_ICON[current.source] || SOURCE_ICON.other).char}
              </span>
              <span className="text-[#0F0E0E] font-medium">{current.author}</span>
              <span className="text-[#6A5F58]">
                · {(SOURCE_ICON[current.source] || SOURCE_ICON.other).label}
              </span>
            </div>
            </div>

            {reviews.length > 1 && (
              <>
                <button
                  onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full text-[#6A5F58] hover:bg-[#F5EFE6] hover:text-[#0F0E0E] transition"
                  aria-label="Previous"
                  data-testid="review-prev"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % reviews.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full text-[#6A5F58] hover:bg-[#F5EFE6] hover:text-[#0F0E0E] transition"
                  aria-label="Next"
                  data-testid="review-next"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="mt-6 flex items-center justify-center gap-1.5">
                  {reviews.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === idx ? "w-6 bg-[#B76E79]" : "w-1.5 bg-[#DDD1BE]"
                      }`}
                      aria-label={`Go to review ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {!hasReviews && (
          <p className="mt-6 text-[#3A322E] max-w-2xl mx-auto text-base">
            From lakeside sunrises to trophy carp, every review is a fresh story from the fenland.
          </p>
        )}

        {/* External links — symmetric pill row */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
          {socials.googleReviews && (
            <a
              href={socials.googleReviews}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google"
              className="social-pill"
            >
              <span className="social-badge" style={{ background: "#0F0E0E", color: "#F5EFE6" }}>G</span>
              <span className="social-label">Google</span>
            </a>
          )}
          {socials.pitchup && (
            <a
              href={socials.pitchup}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-pitchup"
              className="social-pill"
            >
              <span className="social-badge" style={{ background: "#B76E79", color: "#FFFFFF" }}>P</span>
              <span className="social-label">Pitchup</span>
            </a>
          )}
          {socials.facebook && (
            <a
              href={socials.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-facebook"
              className="social-pill"
            >
              <span className="social-badge" style={{ background: "#1877F2", color: "#FFFFFF" }}>f</span>
              <span className="social-label">Facebook</span>
            </a>
          )}
          {socials.instagram && (
            <a
              href={socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-instagram"
              className="social-pill"
            >
              <span
                className="social-badge text-white"
                style={{ background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 50%,#8134AF 100%)" }}
              >
                I
              </span>
              <span className="social-label">Instagram</span>
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
